<template>
    <div class="dialog pn-duc-deg-dialog" @click.stop="">
        <span class="close-btn-container">
            <b-button class="btn-close-dialog" variant="dark" @click="hide">
                <i class="fa fa-times" />
            </b-button>
        </span>

        <span class="pn-label">{{ $translate('pn_count') }}</span>

        <div class="pn-input">
            <b-button class="minus" :disabled="selectedCount === 0" @click="changeCount(-1)">
                -
            </b-button>

            <span class="value">{{ selectedCount }}</span>

            <b-button class="plus" :disabled="selectedCount >= 10" @click="changeCount(1)">
                +
            </b-button>

            <span v-b-tooltip.hover :title="$translate('pn_count_found_at')">
                {{ isNoImageLocated || selectedCount === 0 ? $translate('na') : `${pnTime} H` }}
            </span>

            <b-button class="save-btn" :disabled="!isPnChanged" variant="primary" @click="updatePN">
                {{ $translate('save') }}
            </b-button>
        </div>

        <b-checkbox v-model="isNoImageLocated" class="pn-checkbox">
            {{ $translate('save_without_pn_time') }}
        </b-checkbox>

        <span class="duc-label">{{ $translate('duc_x', 1) }}</span>

        <div class="pill-container duc-pill">
            <pill-switch
                input-id="duc_switch"
                :is-checked="selectedDUC"
                @changed="selectedDUC = $event"
            />
            <b-button
                class="save-btn"
                :disabled="!isDucChanged"
                variant="primary"
                @click="updateDUC"
            >
                {{ $translate('save') }}
            </b-button>
        </div>

        <b-checkbox v-if="selectedDUC" v-model="excludeFromRanking" class="exclude-checkbox">
            {{ $translate('exclude_from_ranking') }}
        </b-checkbox>

        <span class="deg-label">{{ $translate('deg') }}</span>

        <div class="pill-container deg-pill">
            <pill-switch
                input-id="deg_switch"
                :is-checked="selectedIsDegenerated"
                @changed="selectedIsDegenerated = $event"
            />
            <b-button
                class="save-btn"
                :disabled="selectedIsDegenerated === isDegenerated"
                variant="primary"
                @click="updateDeg"
            >
                {{ $translate('save') }}
            </b-button>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import UIUtils from '../../../business/utils/UIUtils'
import BackendProvider from '../../../business/services/BackendProvider'
import PillSwitch from '../common/PillSwitch'

export default {
    name: 'PnDucDegDialog',
    components: { PillSwitch },
    props: {
        well: {
            type: Object,
            required: true
        },
        pnCount: {
            type: Number,
            required: true
        },
        pnCountTime: {
            type: String,
            required: true
        },
        hasDuc: {
            type: Boolean,
            required: true
        },
        isDegenerated: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            selectedCount: this.pnCount,
            selectedDUC: this.hasDuc,
            excludeFromRanking: this.well.excludeFromRanking || false,
            selectedIsDegenerated: this.isDegenerated,
            isNoImageLocated: false
        }
    },
    computed: {
        ...mapState({
            standaloneVideoCurrentTime_H: state => state.video.standaloneVideoCurrentTime_H
        }),
        isPnChanged() {
            return (
                this.pnCount !== this.selectedCount ||
                (this.isNoImageLocated && this.pnCountTime) ||
                (!this.isNoImageLocated && this.pnTime !== parseFloat(this.pnCountTime).toFixed(2))
            )
        },
        pnTime() {
            if (this.isNoImageLocated || this.selectedCount === 0) {
                return null
            } else if (this.selectedCount === this.pnCount && this.pnCountTime) {
                return parseFloat(this.pnCountTime).toFixed(2)
            }
            return this.standaloneVideoCurrentTime_H
        },
        isDucChanged() {
            return (
                this.selectedDUC !== this.hasDuc ||
                this.excludeFromRanking !== !!this.well.excludeFromRanking
            )
        }
    },
    watch: {
        well() {
            this.hide()
        },
        selectedDUC(newVal) {
            if (!newVal) this.excludeFromRanking = false
        }
    },
    mounted() {
        this.isNoImageLocated = this.pnCountTime == null
    },
    methods: {
        changeCount(value) {
            this.selectedCount = this.selectedCount + value
        },
        async updatePN() {
            this.$log.actionEvent('change_well_pn_count')

            const pnCount = {
                value: this.selectedCount,
                time: this.isNoImageLocated
                    ? null
                    : this.pnTime
                    ? this.pnTime.toString()
                    : this.pnCountTime.toString()
            }

            await this.saveChanges({ pnCount })
        },
        async updateDUC() {
            this.$log.actionEvent('change_well_duc')

            UIUtils.showLoadingMask()
            const promises = []

            if (this.selectedDUC !== this.hasDuc) {
                promises.push(
                    BackendProvider.updateInference(this.well._id, {
                        duc: { duc1: this.selectedDUC }
                    })
                )
            }
            if (this.excludeFromRanking !== this.well.excludeFromRanking) {
                promises.push(
                    BackendProvider.updateWell(this.well._id, {
                        excludeFromRanking: this.excludeFromRanking
                    })
                )
            }

            if (promises.length) {
                await Promise.all(promises)
                this.$store.commit('timelapse/triggerWellReFetch', true)
            }

            this.$emit('hide')
        },
        async updateDeg() {
            this.$log.actionEvent('change_well_deg')

            await this.saveChanges({ degeneratedOocyte: this.selectedIsDegenerated })
        },
        async saveChanges(delta) {
            UIUtils.showLoadingMask()

            await BackendProvider.updateInference(this.well._id, delta)

            this.$store.commit('timelapse/triggerWellReFetch', true)
            this.$emit('hide')
        },
        hide() {
            this.$emit('hide')
        }
    }
}
</script>

<style lang="scss" scoped>
.pn-duc-deg-dialog {
    top: 100%;
    color: $light;
    font-size: 14px;
    background-color: $dark-4;
    border: solid 0.1rem $dark;
    cursor: auto;
    // overrides _style
    opacity: 1;
    pointer-events: initial;

    display: grid;
    align-items: center;
    grid-template-columns: auto 1fr;
    grid-template-areas:
        'closebutton  closebutton'
        'pnlabel      pninput'
        'pncheckbox   pncheckbox'
        'duclabel     ducpill'
        'duccheckbox  duccheckbox'
        'deglabel     degpill';

    .close-btn-container {
        grid-area: closebutton;

        .btn-close-dialog {
            width: 2.5rem;
            margin-left: 0.5rem;
            font-size: 1rem;
            text-align: center;
        }
    }

    .save-btn {
        margin-left: auto;
        height: 30px;
        padding: 0 1rem;
    }

    .pn-input {
        grid-area: pninput;
        padding-right: 1rem;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .minus,
        .plus {
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: $dark-5;
            font-size: 20px;
            border-radius: 50%;
            background-color: $light-3 !important;
        }

        .plus {
            margin-right: 1rem;
        }

        .value {
            text-align: center;
            margin: 0 1rem;
        }
    }

    .pn-checkbox,
    .exclude-checkbox {
        margin-left: 1rem;
        margin-bottom: 0.5rem;
    }

    .pill-container {
        height: 100%;
        display: flex;
        align-items: center;
        padding-right: 1rem;
        justify-content: space-between;
    }

    .pn-label,
    .duc-label,
    .deg-label {
        padding: 1rem;
        white-space: nowrap;
    }

    .pn-label {
        grid-area: pnlabel;
    }
    .duc-label {
        grid-area: duclabel;
    }
    .deg-label {
        grid-area: deglabel;
    }

    .deg-pill {
        grid-area: degpill;
    }
    .duc-pill {
        grid-area: ducpill;
    }

    .pn-checkbox {
        grid-area: pncheckbox;
    }
    .exclude-checkbox {
        grid-area: duccheckbox;
    }

    .duc-label,
    .duc-pill,
    .deg-label,
    .deg-pill {
        border-top: solid 0.1rem $dark;
    }
}
</style>

<style lang="scss">
.pn-duc-deg-dialog {
    .pn-checkbox,
    .exclude-checkbox {
        .custom-control-label {
            cursor: pointer;
        }
        .custom-control-input {
            &:not(:checked) ~ .custom-control-label {
                color: $blue;
            }
            &:checked ~ .custom-control-label {
                color: $white;
            }
        }
    }
    .pill-switch {
        display: grid;
        grid-template-columns: 1fr 4fr;

        .custom-control-label {
            &:before {
                height: 1.75rem !important;
                width: 3rem !important;
                top: -0.25rem;
            }
            &:after {
                margin-top: 0.2rem !important;
                height: 1rem !important;
                width: 1rem !important;
                top: -0.15rem;
            }
        }

        .custom-switch {
            .custom-control-input {
                &:not(:checked) ~ .custom-control-label {
                    &:before {
                        background-color: $light;
                    }
                    &:after {
                        background-color: $white;
                        border-color: $white;
                    }
                }
                &:checked ~ .custom-control-label {
                    &:after {
                        margin-left: 0.6rem;
                    }
                }
            }
        }
    }
}
</style>
